import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Close } from "@dreamhub/designsystem/icons";
import { DrawerTitle as CustomDrawerTitle } from "../../FieldManagement/components/DrawerTitle";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FormFieldNewType } from "@/components/controls/Form/types";
import { useDealStages } from "@/entities/settings/deal/hooks";
import {
  DealStage,
  QuestionnaireUpdateInput,
  DealQuestionnaire
} from "@/pages/deals/types";
import { useUpdateQuestionnaireMutation } from "@/pages/deals/hooks";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { TFunction } from "i18next";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import RemoveFromStageModal from "@/pages/settings/components/dealsSettings/StageStudio/workflow/components/RemoveFromStageModal";

const TRANSLATION_PREFIX = "settings.deals.workflow.editWorkflowDetails";

export type confirmationModalType =
  | "moveWorkflowForward"
  | "moveWorkflowBackward";

const getWorkflowTypeOptions = (t: TFunction<"translation", undefined>) => {
  return Object.values(QuestionnaireType).map((questionnaireType) => ({
    id: questionnaireType,
    displayName: t(
      `settings.deals.workflow.workflowDetailedView.workflowTypes.${questionnaireType}`
    )
  }));
};

interface EditWorkflowDetailsDrawerProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  questionnaire?: DealQuestionnaire | null;
  stageId?: string;
  onSave?: (data: any) => void;
}

export const EditWorkflowDetailsDrawer: React.FC<
  EditWorkflowDetailsDrawerProps
> = ({ isOpen, onOpenChange, questionnaire, stageId, onSave }) => {
  const { t } = useTranslation();
  const formRef = useRef<HTMLFormElement>(null);
  const updateQuestionnaireMutation = useUpdateQuestionnaireMutation();

  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formSetValue, setFormSetValue] = useState<any>(null);
  const [formGetValues, setFormGetValues] = useState<any>(null);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);
  const [pendingStageChangeData, setPendingStageChangeData] = useState<{
    originalStageId: string;
    newStageId: string;
    modalType: confirmationModalType;
    onConfirm: () => void;
  } | null>(null);

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

  useEffect(() => {
    if (questionnaire && formSetValue) {
      formSetValue("workflowName", questionnaire.name || "");
      formSetValue("workflowDescription", questionnaire.description);
      formSetValue("stage", stageId || "");
      formSetValue(
        "workflowType",
        questionnaire?.typeValue || QuestionnaireType.SIMPLE
      );
    }
  }, [questionnaire, stageId, formSetValue]);

  const handleClose = () => {
    if (formSetValue) {
      formSetValue("workflowName", "");
      formSetValue("workflowDescription", "");
      formSetValue("stage", "");
      formSetValue("workflowType", QuestionnaireType.SIMPLE);
    }
    setIsFormValid(false);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (!isFormValid || !questionnaire?.id || !formGetValues) return;

    setIsSubmitting(true);
    try {
      const formValues = formGetValues();
      const updateData: QuestionnaireUpdateInput = {
        name: formValues.workflowName?.trim(),
        stageId: formValues.stage ? Number(formValues.stage) : undefined,
        questionnaireOrder: questionnaire.questionnaireOrder,
        kpiValue: questionnaire.kpiValue || undefined,
        typeValue: questionnaire?.typeValue || QuestionnaireType.SIMPLE,
        relatedQuestionnaireId:
          questionnaire.relatedQuestionnaireId || undefined,
        description: formValues.workflowDescription
      };

      const result = await updateQuestionnaireMutation.mutateAsync({
        pathParams: { questionnaireId: questionnaire.id },
        data: updateData
      });

      onSave?.(result);
      handleClose();
    } catch (error) {
      console.error("Failed to update questionnaire:", error);
      // TODO: Add user-facing error notification
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleShowDeleteConfirmation = () => {
    setIsDeleteConfirmed(false);
    setIsDeleteConfirmationModalOpen(true);
  };

  const { data: stagesData } = useDealStages();

  const stageOptions = useMemo(() => {
    if (!stagesData?.stages) return [];

    return stagesData.stages.map((stage: DealStage) => ({
      id: stage.id.toString(),
      displayName: stage.name
    }));
  }, [stagesData]);

  const workflowTypeOptions = useMemo(() => getWorkflowTypeOptions(t), [t]);

  const getStageMovementType = (
    currentStageId: string,
    newStageId: string
  ): confirmationModalType => {
    if (!stagesData?.stages) return "moveWorkflowForward";

    const currentStage = stagesData.stages.find(
      (stage) => stage.id.toString() === currentStageId
    );
    const newStage = stagesData.stages.find(
      (stage) => stage.id.toString() === newStageId
    );

    if (!currentStage || !newStage) return "moveWorkflowForward";

    return newStage.stageOrder > currentStage.stageOrder
      ? "moveWorkflowForward"
      : "moveWorkflowBackward";
  };

  const handleStageSelection = (newStageId: string) => {
    const currentStageId = stageId || "";

    if (currentStageId === newStageId) {
      return;
    }

    const modalType = getStageMovementType(currentStageId, newStageId);

    setPendingStageChangeData({
      originalStageId: currentStageId,
      newStageId,
      modalType,
      onConfirm: () => {
        if (formSetValue) {
          formSetValue("stage", newStageId);
        }
      }
    });
    setIsConfirmationModalOpen(true);

    setTimeout(() => {
      if (formSetValue) {
        formSetValue("stage", currentStageId);
      }
    }, 0);
  };

  const handleConfirmStageChange = () => {
    if (pendingStageChangeData) {
      pendingStageChangeData.onConfirm();
      setPendingStageChangeData(null);
    }
    setIsConfirmationModalOpen(false);
  };

  const handleCancelStageChange = () => {
    if (pendingStageChangeData && formSetValue) {
      formSetValue("stage", pendingStageChangeData.originalStageId);
    }
    setPendingStageChangeData(null);
    setIsConfirmationModalOpen(false);
  };

  const formSections: FormSectionProps[] = [
    {
      id: "workflow-details",
      divider: true,

      dividerClassName: "border-none",
      fields: [
        {
          name: "workflowName",
          type: "text" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowName`),
          required: true,
          fieldActionProps: {
            placeholder: t(`${TRANSLATION_PREFIX}.workflowNamePlaceholder`)
          }
        },
        {
          name: "workflowDescription",
          type: "textarea" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowDescription`),
          required: false,
          fieldActionProps: {
            placeholder: t(
              `${TRANSLATION_PREFIX}.workflowDescriptionPlaceholder`
            ),
            variant: "form",
            minRows: 1
          }
        },
        {
          name: "stage",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.stage`),
          required: true,
          fieldActionProps: {
            fieldName: "stage",
            options: stageOptions,
            placeholder: t(`${TRANSLATION_PREFIX}.stagePlaceholder`),
            onSelection: handleStageSelection
          }
        },
        {
          name: "workflowType",
          type: "singleSelect" as const,
          label: t(`${TRANSLATION_PREFIX}.workflowType`),
          required: true,
          fieldActionProps: {
            fieldName: "workflowType",
            options: workflowTypeOptions,
            disabled: true
          }
        }
      ] as FormFieldNewType[]
    }
  ];

  return (
    <>
      <Drawer
        open={isOpen}
        onOpenChange={onOpenChange}
        direction="right"
        modal={false}
        dismissible={false}
      >
        <AppDrawerContent isSettingsDrawer>
          <DrawerHeader>
            <DrawerTitle>
              <CustomDrawerTitle title={t(`${TRANSLATION_PREFIX}.title`)} />
            </DrawerTitle>
            <IconButton
              data-test="close-edit-workflow-details-drawer"
              onClick={handleClose}
              variant="iconNoBg"
              size="md"
            >
              <Close />
            </IconButton>
          </DrawerHeader>

          <div className="p-5 w-full overflow-y-auto max-h-[calc(100vh-200px)]">
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={(formMethods) => {
                setFormSetValue(() => formMethods.setValue);
                setFormGetValues(() => formMethods.getValues);
              }}
            />
          </div>

          <DrawerFooter>
            <div className="flex justify-between gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
              <Button
                variant="tertiary"
                onClick={handleShowDeleteConfirmation}
                className="h-9 text-red-600 hover:text-red-700"
              >
                {t(`${TRANSLATION_PREFIX}.removeFromStage`)}
              </Button>
              <div className="flex gap-2 ml-auto">
                <Button
                  variant="secondaryNoBg"
                  onClick={handleClose}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.cancel`)}
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!isFormValid || isSubmitting}
                  className="h-9"
                >
                  {t(`${TRANSLATION_PREFIX}.saveChanges`)}
                </Button>
              </div>
            </div>
          </DrawerFooter>
        </AppDrawerContent>
      </Drawer>
      {pendingStageChangeData && (
        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          setIsOpen={setIsConfirmationModalOpen}
          title={t(
            `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.title`
          )}
          confirmationText={
            <div className="flex flex-col gap-4">
              <span>
                {t(
                  `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.message`
                )}
              </span>
              <span>
                {t(
                  `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.message2`
                )}
              </span>
            </div>
          }
          handleSubmit={handleConfirmStageChange}
          handleCancel={handleCancelStageChange}
          isPrimaryButton={true}
          submitButtonText={t(
            `${TRANSLATION_PREFIX}.${pendingStageChangeData.modalType}.confirm`
          )}
        />
      )}
      <RemoveFromStageModal
        isOpen={isDeleteConfirmationModalOpen}
        setIsOpen={setIsDeleteConfirmationModalOpen}
        isDeleteConfirmed={isDeleteConfirmed}
        setIsDeleteConfirmed={setIsDeleteConfirmed}
        stageId={stageId}
        questionnaireId={questionnaire?.id}
        onDelete={handleClose}
      />
    </>
  );
};
